<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="680"
    persistent
    scrollable
    content-class="export-dialog-wrapper">
    <v-card class="export-dialog">
      <!-- Premium Header -->
      <div class="export-header">
        <div class="header-content">
          <div class="header-icon-wrapper">
            <v-icon size="24" color="white">mdi-database-export</v-icon>
          </div>
          <div class="header-text">
            <h2 class="header-title">Export Data</h2>
            <p class="header-subtitle">{{ register ? register.name : 'Register' }}</p>
          </div>
        </div>
        <v-btn
          icon
          small
          class="close-btn"
          @click="closeDialog"
          :disabled="isExporting">
          <v-icon size="20">mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Progress Stepper - Clean Professional Design -->
      <div class="progress-stepper">
        <template v-for="(step, index) in steps">
          <!-- Step Item -->
          <div
            :key="`step-${index}`"
            class="step-item"
            :class="{
              'step-active': currentStep === index + 1,
              'step-complete': currentStep > index + 1,
              'step-clickable': !isExporting && currentStep > index + 1
            }"
            @click="goToStep(index + 1)">
            <div class="step-circle">
              <v-icon v-if="currentStep > index + 1" size="14">mdi-check</v-icon>
              <span v-else>{{ index + 1 }}</span>
            </div>
            <span class="step-label">{{ step }}</span>
          </div>
          <!-- Connector between steps -->
          <div
            v-if="index < steps.length - 1"
            :key="'connector-' + index"
            class="step-connector">
            <div class="connector-fill" :class="{ 'filled': currentStep > index + 1 }"></div>
          </div>
        </template>
      </div>

      <!-- Step Content Area -->
      <div class="step-content-area">
        <!-- Step 1: Export Options -->
        <transition name="step-fade" mode="out-in">
          <div v-if="currentStep === 1" key="step1" class="step-content">
            <!-- Export Mode Banner -->
            <div class="mode-banner" :class="exportMode === ExportMode.Selected ? 'mode-selected' : 'mode-filtered'">
              <div class="mode-icon">
                <v-icon size="22" color="white">
                  {{ exportMode === ExportMode.Selected ? 'mdi-checkbox-multiple-marked' : 'mdi-filter-variant' }}
                </v-icon>
              </div>
              <div class="mode-text">
                <span class="mode-title">{{ exportModeLabel }}</span>
                <span class="mode-description">{{ exportModeDescription }}</span>
              </div>
              <div class="mode-count">
                <span class="count-value">{{ exportMode === ExportMode.Selected ? selectedRowIds.length : effectiveTotalRows }}</span>
                <span class="count-label">rows</span>
              </div>
            </div>

            <!-- Format Selection -->
            <div class="section-block">
              <div class="section-header">
                <span class="section-title">Choose Format</span>
              </div>
              <div class="format-cards">
                <div
                  v-for="option in formatOptions"
                  :key="option.value"
                  class="format-card"
                  :class="{ 'format-selected': selectedFormat === option.value }"
                  @click="selectedFormat = option.value">
                  <div class="format-icon-wrapper" :class="option.value === ExportFormat.Xlsx ? 'excel-icon' : 'csv-icon'">
                    <v-icon size="28" color="white">
                      {{ option.value === ExportFormat.Xlsx ? 'mdi-file-excel' : 'mdi-file-delimited' }}
                    </v-icon>
                  </div>
                  <div class="format-details">
                    <span class="format-name">{{ option.value === ExportFormat.Xlsx ? 'Excel' : 'CSV' }}</span>
                    <span class="format-ext">{{ option.value === ExportFormat.Xlsx ? '.xlsx' : '.csv' }}</span>
                  </div>
                  <div class="format-check">
                    <v-icon v-if="selectedFormat === option.value" size="20" color="primary">mdi-check-circle</v-icon>
                    <div v-else class="check-placeholder"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </transition>

        <!-- Step 2: Field Mapping -->
        <transition name="step-fade" mode="out-in">
          <div v-if="currentStep === 2" key="step2" class="step-content">
            <div class="mapping-header">
              <div class="mapping-header-content">
                <v-icon size="20" class="mapping-icon">mdi-table-cog</v-icon>
                <div>
                  <h3 class="mapping-title">Configure Columns</h3>
                  <p class="mapping-subtitle">Select, rename, and reorder columns for your export</p>
                </div>
              </div>
              <div class="mapping-stats">
                <span class="stats-value">{{ includedColumnsCount }}</span>
                <span class="stats-label">/ {{ effectiveColumns.length }} columns</span>
              </div>
            </div>

            <!-- Loading state when fetching register data -->
            <div v-if="isLoadingRegisterData" class="loading-register-data">
              <v-progress-circular indeterminate color="primary" size="24" />
              <span class="ml-3">Loading register columns...</span>
            </div>

            <ExportFieldMapping
              v-else
              ref="fieldMappingRef"
              :columns="effectiveColumns"
              v-model="fieldMapping"
              @change="onFieldMappingChange" />
          </div>
        </transition>

        <!-- Step 3: Export -->
        <transition name="step-fade" mode="out-in">
          <div v-if="currentStep === 3" key="step3" class="step-content step-3-content">
            <!-- Exporting state -->
            <div v-if="isExporting" class="export-state export-loading">
              <div class="loading-visual">
                <div class="loading-ring"></div>
                <v-progress-circular
                  indeterminate
                  color="primary"
                  size="52"
                  width="3" />
              </div>
              <div class="state-text">
                <h3 class="state-title">Generating Export</h3>
                <p class="state-subtitle">Preparing your data file...</p>
              </div>
            </div>

            <!-- Success state -->
            <div v-else-if="exportComplete" class="export-state export-success">
              <div class="success-visual">
                <div class="success-ring"></div>
                <div class="success-icon">
                  <v-icon size="36" color="white">mdi-check</v-icon>
                </div>
              </div>
              <div class="state-text">
                <h3 class="state-title success-title">Export Complete</h3>
                <p class="state-subtitle">Your file has been downloaded</p>
              </div>
              <v-btn
                color="primary"
                depressed
                class="action-btn mt-5"
                @click="closeDialog">
                Done
              </v-btn>
            </div>

            <!-- Error state -->
            <div v-else-if="exportError" class="export-state export-error">
              <div class="error-visual">
                <div class="error-icon">
                  <v-icon size="32" color="white">mdi-alert</v-icon>
                </div>
              </div>
              <div class="state-text">
                <h3 class="state-title error-title">Export Failed</h3>
                <p class="state-subtitle error-msg">{{ exportError }}</p>
              </div>
              <v-btn
                outlined
                color="primary"
                class="action-btn mt-5"
                @click="currentStep = 1">
                <v-icon left small>mdi-arrow-left</v-icon>
                Try Again
              </v-btn>
            </div>

            <!-- Ready state -->
            <div v-else class="export-ready">
              <!-- Summary Cards -->
              <div class="summary-row">
                <div class="summary-card" style="--delay: 0ms">
                  <div class="card-icon format-bg">
                    <v-icon size="18" color="white">
                      {{ selectedFormat === ExportFormat.Xlsx ? 'mdi-file-excel' : 'mdi-file-delimited' }}
                    </v-icon>
                  </div>
                  <div class="card-content">
                    <span class="card-label">Format</span>
                    <span class="card-value">{{ selectedFormat === ExportFormat.Xlsx ? 'Excel' : 'CSV' }}</span>
                  </div>
                </div>

                <div class="summary-card" style="--delay: 60ms">
                  <div class="card-icon columns-bg">
                    <v-icon size="18" color="white">mdi-view-column</v-icon>
                  </div>
                  <div class="card-content">
                    <span class="card-label">Columns</span>
                    <span class="card-value">{{ includedColumnsCount }} <span class="card-total">/ {{ effectiveColumns.length }}</span></span>
                  </div>
                </div>

                <div class="summary-card" style="--delay: 120ms">
                  <div class="card-icon rows-bg">
                    <v-icon size="18" color="white">mdi-table-row</v-icon>
                  </div>
                  <div class="card-content">
                    <span class="card-label">Rows</span>
                    <span class="card-value">{{ rowCountLabel }}</span>
                  </div>
                </div>
              </div>

              <!-- Download CTA -->
              <div class="download-cta-area">
                <div class="cta-icon-wrapper">
                  <div class="cta-ring"></div>
                  <div class="cta-icon">
                    <v-icon size="28" color="primary">mdi-download</v-icon>
                  </div>
                </div>
                <div class="cta-content">
                  <h3 class="cta-title">Ready to Download</h3>
                  <p class="cta-subtitle">Your export is configured and ready</p>
                </div>
                <v-btn
                  color="primary"
                  large
                  depressed
                  class="download-btn"
                  @click="executeExport"
                  :loading="isExporting">
                  <v-icon left>mdi-download</v-icon>
                  Download {{ selectedFormat === ExportFormat.Xlsx ? 'Excel' : 'CSV' }}
                </v-btn>
              </div>
            </div>
          </div>
        </transition>
      </div>

      <!-- Footer Actions -->
      <div class="dialog-footer" v-if="currentStep < 3 || exportError">
        <v-btn
          text
          class="cancel-btn"
          @click="closeDialog"
          :disabled="isExporting">
          Cancel
        </v-btn>
        <div class="footer-actions">
          <v-btn
            v-if="currentStep > 1"
            text
            class="back-btn"
            @click="currentStep--"
            :disabled="isExporting">
            <v-icon left size="18">mdi-arrow-left</v-icon>
            Back
          </v-btn>
          <v-btn
            v-if="currentStep < 3"
            color="primary"
            depressed
            class="next-btn"
            @click="nextStep"
            :disabled="!canProceed">
            {{ currentStep === 2 ? 'Continue' : 'Next' }}
            <v-icon right size="18">mdi-arrow-right</v-icon>
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import ExportFieldMapping from './ExportFieldMapping.vue';
import { ExportFormat, ExportMode, ExportFormatOptions } from '../../config/enums';
import registerService from '../../services/registerService';
import {
  exportReadScopeFields,
  facilityExportFields,
  isFacilityExportFiltered,
} from '../../modules/registerTableOwnership';
import { registerExportErrorMessage } from '../../modules/registerExportErrors';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';

export default {
  name: 'ExportDialog',
  components: {
    ExportFieldMapping,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    register: {
      type: Object,
      default: null,
    },
    selectedRowIds: {
      type: Array,
      default: () => [],
    },
    currentFilters: {
      type: Object,
      default: () => ({}),
    },
    // Table read scope for Filtered exports: { viewContext, facilityIds?, includeUnassigned? }
    scopeParams: {
      type: Object,
      default: () => ({}),
    },
    tableColumns: {
      type: Array,
      default: () => [],
    },
    exportMode: {
      type: Number,
      default: ExportMode.Filtered,
    },
    totalRows: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      ExportFormat,
      ExportMode,
      formatOptions: ExportFormatOptions,
      steps: ['Options', 'Columns', 'Export'],
      currentStep: 1,
      selectedFormat: ExportFormat.Xlsx,
      fieldMapping: {},
      isExporting: false,
      exportComplete: false,
      exportError: null,
      // Self-fetched data when props are empty
      fetchedColumns: [],
      fetchedTotalRows: 0,
      isLoadingRegisterData: false,
    };
  },
  computed: {
    // Use props if provided, otherwise use self-fetched data
    effectiveColumns() {
      return this.tableColumns.length > 0 ? this.tableColumns : this.fetchedColumns;
    },
    effectiveTotalRows() {
      return this.totalRows > 0 ? this.totalRows : this.fetchedTotalRows;
    },
    // Column filters or the central Facility filter narrow a Filtered export.
    hasExportFilters() {
      const hasColumnFilters = Boolean(this.currentFilters)
        && Object.keys(this.currentFilters).some(k => this.currentFilters[k]);
      return hasColumnFilters || isFacilityExportFiltered(this.scopeParams);
    },
    exportModeLabel() {
      if (this.exportMode === ExportMode.Selected) {
        return `Exporting ${this.selectedRowIds.length} Selected Row${this.selectedRowIds.length !== 1 ? 's' : ''}`;
      }
      return this.hasExportFilters ? 'Exporting Filtered Data' : 'Exporting All Data';
    },
    exportModeDescription() {
      if (this.exportMode === ExportMode.Selected) {
        return 'Only the rows you have selected will be exported.';
      }
      return this.hasExportFilters
        ? 'Only rows matching your current filters will be exported.'
        : 'All rows in this register will be exported.';
    },
    rowCountLabel() {
      if (this.exportMode === ExportMode.Selected) {
        return `${this.selectedRowIds.length}`;
      }
      return this.effectiveTotalRows > 0 ? `${this.effectiveTotalRows}` : 'All';
    },
    includedColumnsCount() {
      if (!this.fieldMapping?.columns) {
        return this.effectiveColumns.length;
      }
      return Object.values(this.fieldMapping.columns).filter(c => c.include).length;
    },
    canProceed() {
      if (this.currentStep === 1) {
        return true;
      }
      if (this.currentStep === 2) {
        return this.includedColumnsCount > 0;
      }
      return true;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.resetState();
          // Fetch register data if tableColumns prop is empty
          if (this.tableColumns.length === 0 && this.register?.id) {
            this.fetchRegisterData();
          }
        }
      },
    },
  },
  methods: {
    resetState() {
      this.currentStep = 1;
      this.selectedFormat = ExportFormat.Xlsx;
      this.fieldMapping = {};
      this.isExporting = false;
      this.exportComplete = false;
      this.exportError = null;
      // Reset fetched data
      this.fetchedColumns = [];
      this.fetchedTotalRows = 0;
      this.isLoadingRegisterData = false;
    },
    async fetchRegisterData() {
      // Don't fetch if tableColumns prop is provided or no register
      if (this.tableColumns.length > 0 || !this.register?.id) return;

      this.isLoadingRegisterData = true;
      try {
        // Use existing API to get register table data
        const response = await registerService.getRegisterTableData(this.register.id, {
          page: 1,
          itemsPerPage: 1, // Just need schema and count, not all data
        });

        this.fetchedColumns = response.columns || [];
        this.fetchedTotalRows = response.totalItems || 0;
      } catch (error) {
        debugError('Failed to fetch register data:', error);
        this.exportError = 'Failed to load register information. Please try again.';
      } finally {
        this.isLoadingRegisterData = false;
      }
    },
    closeDialog() {
      this.$emit('input', false);
      this.$emit('close');
    },
    nextStep() {
      if (this.currentStep < 3) {
        this.currentStep++;
      }
    },
    goToStep(step) {
      if (!this.isExporting && this.currentStep > step) {
        this.currentStep = step;
      }
    },
    onFieldMappingChange(mapping) {
      this.fieldMapping = mapping;
    },
    async executeExport() {
      if (!this.register?.id) {
        this.exportError = 'No register selected';
        return;
      }

      this.isExporting = true;
      this.exportError = null;

      try {
        const exportOptions = {
          format: this.selectedFormat,
          exportMode: this.exportMode,
          selectedRowIds: this.exportMode === ExportMode.Selected ? this.selectedRowIds : [],
          columnFilters: this.exportMode === ExportMode.Filtered ? (this.currentFilters || {}) : {},
          fieldMapping: this.fieldMapping,
          // Every export sends the table's read scope (view context + member Facility); Filtered
          // exports also follow the central Facility filter, Selected exports the chosen rows only.
          ...(this.exportMode === ExportMode.Filtered
            ? facilityExportFields(this.scopeParams)
            : exportReadScopeFields(this.scopeParams)),
        };

        const response = await registerService.exportRegisterAdvanced(this.register.id, exportOptions);

        const extension = this.selectedFormat === ExportFormat.Xlsx ? 'xlsx' : 'csv';
        const defaultFilename = `${this.register.name}_Export_${new Date().toISOString().slice(0, 10)}.${extension}`;
        const filename = registerService.getFilenameFromResponse(response, defaultFilename);

        registerService.downloadBlobAsFile(response.data, filename);

        this.exportComplete = true;
        this.$emit('complete', { filename, format: this.selectedFormat });
      } catch (error) {
        debugError('Export failed:', error);
        // Fixed copy per typed outcome (not configured / forbidden / failed) - never the server text.
        this.exportError = await registerExportErrorMessage(error);
      } finally {
        this.isExporting = false;
      }
    },
  },
};
</script>

<style scoped>
/* ============================================
   EXPORT DIALOG - CLEAN PROFESSIONAL DESIGN
   For engineers and business owners
   ============================================ */

/* Local CSS Variables - Clean, functional design */
.export-dialog {
  /* Core colors from design system */
  --ed-bg-surface: var(--v-bg2-base);
  --ed-bg-elevated: var(--v-bg3-base);
  --ed-text-primary: var(--v-text-base);
  --ed-text-secondary: var(--v-text_secondary-base);
  --ed-border-color: var(--ds-border-default);

  /* Header - Uses app primary green */
  --ed-header-bg: var(--v-primary-base);

  /* Stepper - Clean flat colors (light mode) */
  --ed-stepper-bg: #f8f9fa;
  --ed-step-inactive-bg: #e9ecef;
  --ed-step-inactive-text: #6c757d;
  --ed-step-active-bg: var(--v-primary-base);
  --ed-step-active-text: #ffffff;
  --ed-connector-bg: #dee2e6;
  --ed-connector-filled: var(--v-primary-base);

  /* Cards and sections (light mode) */
  --ed-card-bg: var(--v-bg3-base);
  --ed-card-border: var(--ds-border-default);
  --ed-card-selected-bg: rgba(3, 98, 76, 0.08);
  --ed-card-selected-border: var(--v-primary-base);

  /* Mode banners - use neutral colors matching stepper/footer */
  --ed-mode-filtered-bg: var(--ed-stepper-bg);
  --ed-mode-filtered-border: var(--ed-border-color);
  --ed-mode-selected-bg: var(--ed-stepper-bg);
  --ed-mode-selected-border: var(--ed-border-color);

  /* Footer */
  --ed-footer-bg: #f8f9fa;

  /* Icon colors */
  --ed-icon-color: var(--v-text-base);
  --ed-icon-muted: var(--v-grey-base);

  border-radius: var(--ds-radius-lg);
  overflow: hidden;
  background: var(--ed-bg-surface);
  box-shadow: var(--ds-shadow-3);
}

/* Dark theme overrides */
.theme--dark .export-dialog {
  --ed-header-bg: #3d5a3a;

  --ed-stepper-bg: #252525;
  --ed-step-inactive-bg: #3a3a3a;
  --ed-step-inactive-text: #9ca3af;
  --ed-connector-bg: #4a4a4a;

  --ed-card-bg: #2a2a2a;
  --ed-card-selected-bg: rgba(3, 98, 76, 0.15);

  /* Mode banners inherit from stepper-bg automatically */

  --ed-footer-bg: #252525;

  --ed-icon-color: #ffffff;
  --ed-icon-muted: #9ca3af;
}

/* ============================================
   HEADER - Clean with app primary color
   ============================================ */
.export-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--ds-space-md) var(--ds-space-lg);
  background: var(--ed-header-bg);
}

.header-content {
  display: flex;
  align-items: center;
  gap: var(--ds-space-sm);
}

.header-icon-wrapper {
  width: 36px;
  height: 36px;
  border-radius: var(--ds-radius-md);
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-text {
  display: flex;
  flex-direction: column;
}

.header-title {
  font-size: var(--ds-font-subtitle);
  font-weight: 600;
  color: #ffffff;
  margin: 0;
}

.header-subtitle {
  font-size: var(--ds-font-caption);
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
}

.close-btn {
  color: rgba(255, 255, 255, 0.8) !important;
}

.close-btn:hover {
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.15) !important;
}

/* ============================================
   STEPPER - Clean professional design
   Labels centered under step circles
   ============================================ */
.progress-stepper {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--ds-space-lg) var(--ds-space-xl);
  background: var(--ed-stepper-bg);
  border-bottom: 1px solid var(--ed-border-color);
  gap: 0;
}

.step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 80px;
}

.step-circle {
  width: 32px;
  height: 32px;
  border-radius: var(--ds-radius-full);
  background: var(--ed-step-inactive-bg);
  color: var(--ed-step-inactive-text);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--ds-font-body-sm);
  font-weight: 600;
  transition: all 0.2s ease;
}

.step-circle .v-icon {
  color: var(--ed-step-active-text) !important;
}

.step-active .step-circle {
  background: var(--ed-step-active-bg);
  color: var(--ed-step-active-text);
  position: relative;
}

/* Pulse animation for active step - subtle professional effect */
.step-active .step-circle::after {
  content: '';
  position: absolute;
  inset: -3px;
  border-radius: var(--ds-radius-full);
  border: 2px solid var(--v-primary-base);
  opacity: 0.5;
  animation: step-pulse 2s ease-out infinite;
}

@keyframes step-pulse {
  0% { transform: scale(1); opacity: 0.5; }
  100% { transform: scale(1.25); opacity: 0; }
}

.step-complete .step-circle {
  background: var(--ed-step-active-bg);
  color: var(--ed-step-active-text);
}

.step-clickable {
  cursor: pointer;
}

.step-clickable:hover .step-circle {
  opacity: 0.85;
}

.step-connector {
  flex: 1;
  height: 2px;
  background: var(--ed-connector-bg);
  min-width: 40px;
  max-width: 80px;
  margin: 0 var(--ds-space-xs);
  align-self: flex-start;
  margin-top: 15px; /* Center with step circle */
}

.connector-fill {
  height: 100%;
  width: 0;
  background: var(--ed-connector-filled);
  transition: width 0.3s ease;
}

.connector-fill.filled {
  width: 100%;
}

.step-label {
  margin-top: var(--ds-space-xs);
  font-size: var(--ds-font-micro);
  font-weight: 500;
  color: var(--ed-step-inactive-text);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-align: center;
}

.step-active .step-label {
  color: var(--v-primary-base);
  font-weight: 600;
}

.step-complete .step-label {
  color: var(--ed-text-primary);
}

/* Step Content Area */
.step-content-area {
  background: var(--ed-bg-surface);
}

.step-content {
  padding: var(--ds-space-lg);
}

.step-3-content {
  padding: 0;
}

/* Step Transition */
.step-fade-enter-active,
.step-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.step-fade-enter {
  opacity: 0;
  transform: translateX(20px);
}

.step-fade-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

/* ============================================
   STEP 1: EXPORT OPTIONS
   ============================================ */

/* ============================================
   STEP 1: EXPORT OPTIONS - Mode Banner
   ============================================ */
.mode-banner {
  display: flex;
  align-items: center;
  gap: var(--ds-space-md);
  padding: var(--ds-space-md);
  border-radius: var(--ds-radius-md);
  margin-bottom: var(--ds-space-lg);
}

.mode-banner.mode-selected {
  background: var(--ed-mode-selected-bg);
  border: 1px solid var(--ed-mode-selected-border);
}

.mode-banner.mode-filtered {
  background: var(--ed-mode-filtered-bg);
  border: 1px solid var(--ed-mode-filtered-border);
}

.mode-icon {
  width: 40px;
  height: 40px;
  border-radius: var(--ds-radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.mode-selected .mode-icon {
  background: var(--v-blue-base);
}

.mode-filtered .mode-icon {
  background: var(--v-primary-base);
}

.mode-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.mode-title {
  font-size: var(--ds-font-body);
  font-weight: 600;
  color: var(--ed-text-primary);
}

.mode-description {
  font-size: var(--ds-font-caption);
  color: var(--ed-text-secondary);
}

.mode-count {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--ds-space-sm) var(--ds-space-md);
  background: var(--ed-card-bg);
  border-radius: var(--ds-radius-md);
  border: 1px solid var(--ed-border-color);
}

.count-value {
  font-size: var(--ds-font-title);
  font-weight: 700;
  color: var(--ed-text-primary);
  line-height: 1;
}

.count-label {
  font-size: var(--ds-font-micro);
  color: var(--ed-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* Section Block */
.section-block {
  margin-top: var(--ds-space-sm);
}

.section-header {
  margin-bottom: var(--ds-space-sm);
}

.section-title {
  font-size: var(--ds-font-caption);
  font-weight: 600;
  color: var(--ed-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

/* Format Cards - Clean Professional */
.format-cards {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--ds-space-sm);
}

.format-card {
  display: flex;
  align-items: center;
  gap: var(--ds-space-sm);
  padding: var(--ds-space-md);
  background: var(--ed-card-bg);
  border: 2px solid var(--ed-border-color);
  border-radius: var(--ds-radius-md);
  cursor: pointer;
  transition: all 0.2s ease;
}

.format-card:hover {
  border-color: var(--v-primary-base);
}

.format-card.format-selected {
  border-color: var(--ed-card-selected-border);
  background: var(--ed-card-selected-bg);
}

.format-icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: var(--ds-radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.format-icon-wrapper.excel-icon {
  background: linear-gradient(135deg, #48bb78 0%, #276749 100%);
}

.format-icon-wrapper.csv-icon {
  background: linear-gradient(135deg, #667eea 0%, #5a67d8 100%);
}

.format-details {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.format-name {
  font-size: var(--ds-font-subtitle);
  font-weight: 600;
  color: var(--ed-text-primary);
}

.format-ext {
  font-size: var(--ds-font-caption);
  color: var(--ed-text-secondary);
}

.format-check {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.check-placeholder {
  width: 20px;
  height: 20px;
  border-radius: var(--ds-radius-full);
  border: 2px solid var(--ed-border-color);
}

/* ============================================
   STEP 2: FIELD MAPPING
   ============================================ */

.mapping-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--ds-space-md);
  background: var(--ed-stepper-bg);
  border-radius: var(--ds-radius-md);
  margin-bottom: var(--ds-space-md);
  border: 1px solid var(--ed-border-color);
}

.mapping-header-content {
  display: flex;
  align-items: center;
  gap: var(--ds-space-sm);
}

.mapping-icon {
  color: var(--v-primary-base) !important;
}

.mapping-title {
  font-size: var(--ds-font-subtitle);
  font-weight: 600;
  color: var(--ed-text-primary);
  margin: 0;
}

.mapping-subtitle {
  font-size: var(--ds-font-caption);
  color: var(--ed-text-secondary);
  margin: 2px 0 0 0;
}

.mapping-stats {
  display: flex;
  align-items: baseline;
  gap: var(--ds-space-xs);
  padding: var(--ds-space-xs) var(--ds-space-sm);
  background: var(--ed-card-bg);
  border-radius: var(--ds-radius-sm);
  border: 1px solid var(--ed-border-color);
}

.stats-value {
  font-size: var(--ds-font-title);
  font-weight: 700;
  color: var(--v-primary-base);
}

.stats-label {
  font-size: var(--ds-font-caption);
  color: var(--ed-text-secondary);
}

/* Loading register data state */
.loading-register-data {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--ds-space-2xl);
  color: var(--ed-text-secondary);
  font-size: var(--ds-font-body);
}

/* ============================================
   STEP 3: EXPORT STATES
   ============================================ */

.export-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--ds-space-2xl) var(--ds-space-lg);
  text-align: center;
}

.state-text {
  margin-top: var(--ds-space-lg);
}

.state-title {
  font-size: var(--ds-font-title);
  font-weight: 600;
  color: var(--ed-text-primary);
  margin: 0 0 6px 0;
}

.state-subtitle {
  font-size: var(--ds-font-body);
  color: var(--ed-text-secondary);
  margin: 0;
}

/* Loading State */
.export-loading {
  background: var(--ed-bg-surface);
}

.loading-visual {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-ring {
  display: none; /* Removed - keeping it simple */
}

/* Success State */
.export-success {
  background: rgba(3, 98, 76, 0.08);
}

.theme--dark .export-success {
  background: rgba(3, 98, 76, 0.12);
}

.success-visual {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.success-ring {
  position: absolute;
  width: 80px;
  height: 80px;
  border-radius: var(--ds-radius-full);
  border: 2px solid rgba(3, 98, 76, 0.4);
  animation: success-ring-pulse 2s ease-out infinite;
}

@keyframes success-ring-pulse {
  0% { transform: scale(1); opacity: 1; }
  100% { transform: scale(1.3); opacity: 0; }
}

.success-icon {
  width: 56px;
  height: 56px;
  border-radius: var(--ds-radius-full);
  background: var(--v-primary-base);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: success-pop 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

@keyframes success-pop {
  0% { transform: scale(0); }
  70% { transform: scale(1.1); }
  100% { transform: scale(1); }
}

.success-title {
  color: var(--v-primary-base);
}

/* Error State */
.export-error {
  background: rgba(183, 28, 28, 0.08);
}

.theme--dark .export-error {
  background: rgba(183, 28, 28, 0.12);
}

.error-visual {
  display: flex;
  align-items: center;
  justify-content: center;
}

.error-icon {
  width: 56px;
  height: 56px;
  border-radius: var(--ds-radius-full);
  background: var(--v-error-base);
  display: flex;
  align-items: center;
  justify-content: center;
}

.error-title {
  color: var(--v-error-base);
}

.error-msg {
  color: var(--v-error-base) !important;
  max-width: 300px;
}

/* Ready State */
.export-ready {
  padding: var(--ds-space-lg);
  background: var(--ed-ready-bg);
}

.summary-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--ds-space-sm);
  margin-bottom: var(--ds-space-lg);
}

.summary-card {
  display: flex;
  align-items: center;
  gap: var(--ds-space-sm);
  padding: var(--ds-space-sm) var(--ds-space-md);
  background: var(--ed-card-bg);
  border: 1px solid var(--ed-border-color);
  border-radius: var(--ds-radius-md);
  animation: card-slide-in 0.35s ease-out backwards;
  animation-delay: var(--delay, 0ms);
}

@keyframes card-slide-in {
  0% { opacity: 0; transform: translateY(8px); }
  100% { opacity: 1; transform: translateY(0); }
}

.card-icon {
  width: 36px;
  height: 36px;
  border-radius: var(--ds-radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.card-icon.format-bg {
  background: var(--v-primary-base);
}

.card-icon.columns-bg {
  background: var(--v-blue-base);
}

.card-icon.rows-bg {
  background: var(--v-golden-base);
}

.card-content {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.card-label {
  font-size: var(--ds-font-micro);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--ed-text-secondary);
}

.card-value {
  font-size: var(--ds-font-subtitle);
  font-weight: 700;
  color: var(--ed-text-primary);
}

.card-total {
  font-weight: 400;
  color: var(--ed-text-secondary);
  font-size: var(--ds-font-body-sm);
}

/* Download CTA */
.download-cta-area {
  display: flex;
  align-items: center;
  gap: var(--ds-space-md);
  padding: var(--ds-space-md);
  background: var(--ed-card-bg);
  border: 1px solid var(--ed-border-color);
  border-radius: var(--ds-radius-md);
}

.cta-icon-wrapper {
  position: relative;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.cta-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: var(--ds-radius-full);
  border: 2px dashed rgba(3, 98, 76, 0.4);
  animation: cta-rotate 12s linear infinite;
}

@keyframes cta-rotate {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.cta-icon {
  width: 48px;
  height: 48px;
  border-radius: var(--ds-radius-full);
  background: rgba(3, 98, 76, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
}

.theme--dark .cta-icon {
  background: rgba(3, 98, 76, 0.2);
}

.cta-content {
  flex: 1;
}

.cta-title {
  font-size: var(--ds-font-subtitle);
  font-weight: 600;
  color: var(--ed-text-primary);
  margin: 0 0 2px 0;
}

.cta-subtitle {
  font-size: var(--ds-font-body-sm);
  color: var(--ed-text-secondary);
  margin: 0;
}

.download-btn {
  font-weight: 600;
  text-transform: none;
  padding: 0 var(--ds-space-lg) !important;
  height: 40px !important;
  border-radius: var(--ds-radius-sm) !important;
}

/* ============================================
   FOOTER
   ============================================ */

.dialog-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--ds-space-md) var(--ds-space-lg);
  background: var(--ed-footer-bg);
  border-top: 1px solid var(--ed-border-color);
}

.footer-actions {
  display: flex;
  gap: var(--ds-space-sm);
}

.cancel-btn {
  color: var(--ed-text-secondary) !important;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
}

.back-btn {
  color: var(--ed-text-muted) !important;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
}

.next-btn {
  font-weight: 600;
  text-transform: none;
  padding: 0 var(--ds-space-lg) !important;
  height: 36px !important;
  border-radius: var(--ds-radius-sm) !important;
}

.action-btn {
  font-weight: 600;
  text-transform: none;
  min-width: 100px;
  border-radius: var(--ds-radius-sm) !important;
}

/* ============================================
   RESPONSIVE
   ============================================ */

@media (max-width: 600px) {
  .format-cards {
    grid-template-columns: 1fr;
  }

  .summary-row {
    grid-template-columns: 1fr;
    gap: var(--ds-space-sm);
  }

  .download-cta-area {
    flex-direction: column;
    text-align: center;
    gap: var(--ds-space-md);
  }

  .mode-banner {
    flex-wrap: wrap;
  }

  .mode-count {
    width: 100%;
    margin-top: var(--ds-space-sm);
  }

  .progress-stepper {
    padding: var(--ds-space-md);
  }

  .step-label {
    font-size: 9px;
  }
}
</style>
